import {
  MantineThemeOverride,
  MantineTheme,
  virtualColor,
  colorsTuple,
} from '@mantine/core';

export const theme: MantineThemeOverride = {
  black: '#333',
  colors: {
    // Primary link color shades (lightest to darkest)
    primary: [
      '#ffe6f9', // Lightest
      '#ffb3e6',
      '#ff80d3',
      '#ff4dc0',
      '#ff1aac',
      '#ff00bf', // Original color
      '#e600ac',
      '#cc0099',
      '#b30085',
      '#990073', // Darkest
    ] as const, // This enforces the exact length and structure

    // Hover link color shades (lightest to darkest)
    secondary: [
      '#e6ffe6', // Lightest
      '#b3ffb3',
      '#80ff80',
      '#4dff4d',
      '#1aff1a',
      '#05ff00', // Original hover color
      '#04e600',
      '#03cc00',
      '#02b300',
      '#019900', // Darkest
    ] as const,

    // Body text color shades (lightest to darkest)
    dark: [
      '#f2f2f2', // Lightest
      '#e0e0e0',
      '#cccccc',
      '#b3b3b3',
      '#999999',
      '#808080', // Mid-tone gray
      '#666666',
      '#4d4d4d',
      '#333333', // Original color for body text
      '#1a1a1a', // Darkest
    ] as const,

    pink: [
      '#ff66d9',
      '#ff4dd2',
      '#ff33cc',
      '#ff1ac5',
      '#ff00bf',
      '#e600ac',
      '#cc0099',
      '#b30085',
      '#990070',
      '#80005d',
    ] as const,
    lime: [
      '#69ff66',
      '#50ff4d',
      '#37ff33',
      '#1eff1a',
      '#05ff00',
      '#05e600',
      '#04cc00',
      '#04b300',
      '#039900',
      '#038000',
    ] as const,
    blue: [
      '#66b0ff',
      '#4da3ff',
      '#3395ff',
      '#1a88ff',
      '#007bff',
      '#006fe6',
      '#0062cc',
      '#0056b3',
      '#004a99',
      '#003e80',
    ] as const,
    red: [
      '#fd948b',
      '#fc8277',
      '#fc7164',
      '#fb5f50',
      '#fb4d3d',
      '#e24537',
      '#c93e31',
      '#b0362a',
      '#972e23',
      '#7e261c',
    ] as const,
    orange: [
      '#fff2e6',
      '#ffd1b3',
      '#ffb066',
      '#ff9933',
      '#ff8000',
      '#ff6a00',
      '#ff5500',
      '#e64d00',
      '#cc4400',
      '#b33b00',
    ] as const,

    cyan: [
      '#e0fcff',
      '#b3f5ff',
      '#85eeff',
      '#5fe8ff', // from sidebar menu
      '#33e1ff',
      '#00dbff',
      '#00c5e6',
      '#00afcc',
      '#0099b3',
      '#008299',
    ] as const,
    practistBlue: [
      '#eaefff',
      '#d3daff',
      '#a5b2f7',
      '#7487f0',
      '#566ceb',
      '#304be7',
      '#2140e6',
      '#1232cd',
      '#082cb8',
      '#0025a3',
    ] as const,
    'table-header-bg-dark': colorsTuple('var(--mantine-color-dark-7)'),
    'table-header-bg-light': colorsTuple('var(--mantine-color-gray-1)'),
    'table-header-bg': virtualColor({
      name: 'table-header-bg',
      dark: 'table-header-bg-dark',
      light: 'table-header-bg-light',
    }),
  },
  radius: {
    xs: '2px',
    sm: '4px',
    md: '6px',
    lg: '8px',
    xl: '10px',
  },
  shadows: {
    md: '0 0 0.1875rem rgba(0, 0, 0, 0.10), 0 0 1.5625rem -0.3125rem rgba(0, 0, 0, 0.10), 0 0 0.625rem -0.3125rem rgba(0, 0, 0, 0.09) ',
    xl: '5px 5px 3px rgba(0, 0, 0, .25)',
  },
  headings: {
    sizes: {
      h1: { fontSize: '1.75rem' },
      h2: { fontSize: '1.5rem' },
      h3: { fontSize: '1rem' },
      h4: { fontSize: '0.875rem' },
    },
  },
  spacing: {
    xs: '0.5rem',
    sm: '0.75rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
  },
  fontSizes: {
    xs: '0.8rem',
  },
  components: {
    ActionIcon: {
      defaultProps: {
        color: 'gray',
        variant: 'subtle',
      },
      styles: {
        root: {
          // Override default 'button' styles.
          boxShadow: 'none',
          border: 'none',
          margin: 0,
        },
      },
    },
    Switch: {
      styles: (theme: MantineTheme) => ({
        root: {
          '&:hover': {
            cursor: 'pointer',
          },
        },
      }),
    },
  },
};
